import React, { useContext, useMemo } from 'react';
import { Box, Typography, Grid, Paper, Stack, Button, Fade, useTheme } from '@mui/material';
import CalendarView from "../../components/termine/CalendarView";
import { useNavigate } from 'react-router-dom';
import SportsSoccerRoundedIcon from '@mui/icons-material/SportsSoccerRounded';
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import { useAuth } from '../../context/AuthContext';
import { ActiveTeamContext } from '../../context/ActiveTeamContext';
import useDataFetch from '../../utils/useDataFetch';

const Home = () => {
    const theme = useTheme();
    const { user } = useAuth();
    const { activeTeamId } = useContext(ActiveTeamContext);
    const navigate = useNavigate();
    const { data: termineData } = useDataFetch({ url: activeTeamId && user?.id ? `/termine/allAppointments?id=${user.id}&teamId=${activeTeamId}` : null });

    const nextAppointment = useMemo(() => {
        const appointments = termineData?.appointments || [];
        const now = new Date();

        return appointments
            .filter(t => t.dat)
            .map(t => ({ ...t, dateObj: new Date(t.dat) }))
            .filter(t => t.dateObj >= now)
            .sort((a, b) => a.dateObj - b.dateObj)[0];
    }, [termineData]);

    const nextAppointmentLabel = nextAppointment
        ? `${new Intl.DateTimeFormat('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' }).format(new Date(nextAppointment.dat))} • ${nextAppointment.start_time?.substring(0, 5) || '-'} Uhr`
        : null;

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1400, mx: 'auto', mb: 12 }}>

            {/* Hero Section */}
            <Fade in={true} timeout={800}>
                <Box sx={{ mb: 2 }}>
                    <Typography variant="h3" sx={{ fontWeight: 900, mb: 1, textTransform: 'uppercase', letterSpacing: '-0.02em' }}>
                        Moin, <span style={{ color: theme.palette.primary.main }}>{user?.first_name || 'Spieler'}</span>
                    </Typography>
                    <Typography variant="h6" sx={{ color: 'text.secondary', fontWeight: 500, opacity: 0.8 }}>
                        Bereit für die nächste Einheit? Hier ist dein Überblick.
                    </Typography>
                </Box>
            </Fade>

            <Grid container spacing={3} sx={{ width: '100%' }}>
                {/* Bento Quick Actions */}
                <Grid sx={{ width: '100%' }}>
                    <Stack spacing={3}>
                        {nextAppointment && (
                            <Paper
                                sx={{
                                    p: 3,
                                    borderRadius: "12px",
                                    bgcolor: 'primary.main',
                                    color: '#000',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    '&:hover': { transform: 'scale(1.02)', boxShadow: '0 20px 40px rgba(190, 242, 100, 0.3)' }
                                }}
                                onClick={() => navigate(`/termine/${nextAppointment.id}`)}
                            >
                                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                    <Box>
                                        <Typography variant="h5" sx={{ fontWeight: 900, mb: 0.5 }}>Nächster Termin</Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600, opacity: 0.8 }}>
                                            {nextAppointment.titel || 'Termin'} • {nextAppointmentLabel}
                                        </Typography>
                                    </Box>
                                    <ArrowForwardRoundedIcon />
                                </Stack>
                            </Paper>
                        )}
                    </Stack>
                </Grid>

                {/* Calendar View - Bento Expanded */}
                <Grid sx={{ width: '100%' }}>
                    <Paper
                        sx={{
                            p: { xs: 2, md: 4 },
                            borderRadius: "12px",
                            border: '1px solid',
                            borderColor: 'divider',
                            background: theme.palette.mode === 'dark' ? 'rgba(30, 30, 30, 0.2)' : 'rgba(255, 255, 255, 0.4)',
                            backdropFilter: 'blur(20px)',
                            height: '100%',
                            width: '100%',
                        }}
                    >
                        <CalendarView />
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

export default Home;
